"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { createBasePayment, confirmBasePayment } from "@/utils/api/base";
import BaseWalletButton from "./BaseWalletButton";
import { useAccount, useConfig } from "wagmi";
import { writeContract, waitForTransactionReceipt, readContract } from "@wagmi/core";
import { getBalance, simulateContract, getChainId } from "@wagmi/core";
import { parseAbi } from "viem";
import { base } from "wagmi/chains";
import { useEnsureBase } from "@/hooks/useEnsureBase";
import friendlyBaseError from "@/features/landing/utils/friendlyBaseError";
import RailButton from "@/components/ui/RailButton"; // ✅ NEW

const ERC20_ABI = parseAbi([
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
]);

const CHECKOUT_ABI = parseAbi([
  "function pay(bytes32 ref, uint256 amount) external",
  "event Paid(bytes32 indexed ref, address indexed payer, uint256 amount)",
]);

const ERC20_BALANCE_ABI = parseAbi([
  "function balanceOf(address owner) view returns (uint256)",
]);

async function preflight({ wagmiConfig, address, usdc, amount }) {
  const [ethBal, usdcBal] = await Promise.all([
    getBalance(wagmiConfig, { address, chainId: base.id }),
    readContract(wagmiConfig, {
      chainId: base.id,
      address: usdc,
      abi: ERC20_BALANCE_ABI,
      functionName: "balanceOf",
      args: [address],
    }),
  ]);

  if (ethBal.value === 0n) return { ok: false, kind: "NO_GAS" };
  if (BigInt(usdcBal) < amount) return { ok: false, kind: "NO_USDC" };
  return { ok: true };
}

export default function BasePayButton({ product, user, onIntent }) {
  const { address, isConnected } = useAccount();
  const wagmiConfig = useConfig();
  const { isBase, ensureBase } = useEnsureBase();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [intent, setIntent] = useState(null);
  const isLoggedIn = !!(user?._id || user?.id || user?.sub || user?.email);

  const creatingRef = useRef(false);

  // keep latest onIntent without making effects re-run
  const onIntentRef = useRef(onIntent);
  useEffect(() => {
    onIntentRef.current = onIntent;
  }, [onIntent]);

  // stable key for "this checkout"
  const productKey = useMemo(() => {
    const offer = product?.offerId ?? "";
    const amt = product?.amountUSD ?? product?.amountMajor ?? "";
    const sku = product?.sku ?? product?.productSku ?? "";
    return `${offer}:${sku}:${amt}`;
  }, [product?.offerId, product?.amountUSD, product?.amountMajor, product?.sku, product?.productSku]);

  function normalizeBaseIntent(init) {
  if (!init) return null;

  return {
    ...init,
    txnId: init?.txnId,
    usdc: init?.usdc ?? init?.contracts?.usdc,
    checkoutContract: init?.checkoutContract ?? init?.contracts?.checkoutContract,
    amountMinor: init?.amountMinor ?? init?.payment?.amountMinor,
    referenceBytes32:
      init?.referenceBytes32 ??
      init?.reference?.bytes32 ??
      init?.reference?.referenceBytes32,
  };
}
  // reset intent ONLY when the checkout meaningfully changes
  useEffect(() => {
    setIntent(null);
    onIntentRef.current?.(null);
  }, [productKey]);

  const handlePay = async () => {
    try {
      setLoading(true);
      setErr(null);

      if (!user) {
        toast.info("Please log in to pay with Base USDC.");
        return;
      }

      if (!isConnected || !address) {
        toast.info("Connect your wallet to pay with USDC on Base.");
        return;
      }

      // Force Base (wallet modal)
      if (!isBase) {
        toast.warn("Please switch your wallet to Base to continue.");
        const ok = await ensureBase();
        if (!ok) {
          toast.info("Network switch cancelled. No transaction was sent.");
          return;
        }
        if (getChainId(wagmiConfig) !== base.id) {
          toast.warn("Still not on Base. No transaction was sent.");
          return;
        }
      }

      const email = user?.email || product?.recipient?.email || null;

      // 1) Create intent
       const rawInit = intent || (await createBasePayment({ product, email }));
        const init = normalizeBaseIntent(rawInit);

        if (!intent) setIntent(rawInit);
        onIntentRef.current?.(rawInit);

        const { txnId, usdc, checkoutContract, amountMinor, referenceBytes32 } = init || {};

        if (!txnId || !usdc || !checkoutContract || !amountMinor || !referenceBytes32) {
          console.error("Bad BASE init payload", rawInit);
          throw new Error("BASE init missing required fields");
        }

      const amount = BigInt(amountMinor);
      const pf = await preflight({ wagmiConfig, address, usdc, amount });

      if (!pf.ok) {
        if (pf.kind === "NO_GAS") {
          toast.warn("You need a little ETH on Base to pay network fees (gas). No transaction was sent.");
        } else {
          toast.warn("Not enough USDC on Base for this payment. No transaction was sent.");
        }
        return;
      }

      // 2) Only approve if needed
      const allowance = await readContract(wagmiConfig, {
        chainId: base.id,
        address: usdc,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address, checkoutContract],
      });

      if (BigInt(allowance) < amount) {
        toast.info("Confirm USDC approval in your wallet…");

        if (window?.ethereum?.chainId?.toLowerCase() !== `0x${base.id.toString(16)}`) {
          toast.warn("Wrong network. Switch to Base to continue. No transaction was sent.");
          return;
        }

        const approveHash = await writeContract(wagmiConfig, {
          chainId: base.id,
          address: usdc,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [checkoutContract, amount],
        });

        toast.info("Approval submitted. Waiting for confirmation…");
        await waitForTransactionReceipt(wagmiConfig, { hash: approveHash });
      }

      // 3) Pay
      toast.info("Preparing payment… (nothing has been sent yet)");
      const sim = await simulateContract(wagmiConfig, {
        chainId: base.id,
        address: checkoutContract,
        abi: CHECKOUT_ABI,
        functionName: "pay",
        args: [referenceBytes32, amount],
      });

      toast.info("Check your wallet to confirm (nothing has been sent yet)");
      const payHash = await writeContract(wagmiConfig, sim.request);

      toast.info("Payment submitted. Waiting for confirmation…");
      await waitForTransactionReceipt(wagmiConfig, { hash: payHash });

      // 4) Confirm + fulfill
      toast.info("Confirming…");
      const confirmed = await confirmBasePayment({ txnId, txHash: payHash });

      toast.success("✅ Paid on Base. Delivering…");
      return confirmed;
    } catch (e) {
      const { msg, kind } = friendlyBaseError(e);
      setErr(msg);

      if (kind === "NETWORK" || kind === "ADD_CHAIN") toast.warn(msg);
      else if (kind === "REJECTED") toast.info(msg);
      else toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading;

  const label = loading
    ? "Processing…"
    : !isConnected
      ? "Connect wallet"
      : !isBase
        ? "Switch to Base"
        : "Pay with USDC (Base)";

  return (
    <div className="mt-3 w-full animate-fadeIn">

      {isConnected && !isBase && (
        <div className="mb-2 text-xs text-yellow-400 text-center">
          Wrong network. Please switch to <b>Base</b>.
        </div>
      )}
{!isConnected ? (
  <BaseWalletButton />
) : (
  <>
    {!isBase && (
      <div className="mb-2 text-xs text-yellow-400 text-center">
        Wrong network. Please switch to <b>Base</b>.
      </div>
    )}

    <RailButton
      theme="base"
      loading={loading}
      disabled={loading}
      onClick={handlePay}
      hint={!isBase ? "Switch network to Base" : "Fast USDC checkout"}
      badge="Base / Coinbase"
    >
      {label}
    </RailButton>
  </>
)}

      {err && <p className="mt-2 text-xs text-red-400 text-center">{err}</p>}
    </div>
  );
}
